/**
 * Centralized logging for Firebase Storage upload failures (e.g. 412, storage/unknown).
 * Use in onboarding and profile editor catch blocks to capture full server response and context.
 */

import { storage } from "../../../firebaseConfig.js";

export interface LogStorageErrorContext {
  uid?: string;
  uploadPath?: string;
  projectId?: string;
  storageBucketFromConfig?: string;
  fileType?: string;
  fileSizeBytes?: number;
  flow?: string;
}

/**
 * Logs all relevant error fields and context, then returns a UI-safe error string.
 * CRITICAL: error.serverResponse often contains the actual 412 payload from the server.
 */
export function logStorageError(err: unknown, context?: LogStorageErrorContext): string {
  const e = err as Record<string, unknown> | null | undefined;
  const name = e?.name;
  const code = e?.code;
  const message = e?.message;
  const stack = e?.stack;
  const customData = e?.customData;
  const serverResponse = e?.serverResponse;

  // Log error fields (guarded)
  console.error("[logStorageError] Storage upload failed", {
    error: {
      name,
      code,
      message,
      stack,
      customData,
      serverResponse: serverResponse != null ? String(serverResponse) : serverResponse,
    },
  });

  // Log contextual info (include bucket from storage ref if not in context)
  const storageBucketFromStorage =
    typeof storage?.app?.options?.storageBucket === "string"
      ? storage.app.options.storageBucket
      : undefined;
  const projectIdFromApp =
    context?.projectId ??
    (typeof (storage?.app as { options?: { projectId?: string } })?.options?.projectId === "string"
      ? (storage.app as { options: { projectId: string } }).options.projectId
      : undefined);
  console.error("[logStorageError] Context", {
    uid: context?.uid,
    uploadPath: context?.uploadPath,
    projectId: context?.projectId ?? projectIdFromApp,
    storageBucketFromConfig: context?.storageBucketFromConfig,
    storageBucketFromStorage,
    fileType: context?.fileType,
    fileSizeBytes: context?.fileSizeBytes,
    flow: context?.flow,
  });

  // UI string: code + first 300 chars of serverResponse
  const codeStr = code != null ? String(code) : "storage/unknown";
  const responseStr =
    serverResponse != null && serverResponse !== ""
      ? String(serverResponse).slice(0, 300)
      : "(no server response)";
  return `${codeStr} — ${responseStr}`;
}
